import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * <Generic6DOFJoint3D>: a Node3D group that draws the travel of its three axes and three circles per
 * resolved body for every node, faint while unselected (ADR-0056): each circle follows the angular
 * limit of its axis and looks from the joint at its body (`joint_3d_gizmo_plugin.cpp:582-723`).
 */
import { useCallback, useMemo } from 'react';
import { useByValue } from '../../../../r3f/hooks/useByValue';
import { identityTransform3D } from '../../../../godot/math/transform3d';
import { Node3D } from '../../../base/node3d/Component';
import { PluginGizmo } from '../../../../r3f/gizmo/PluginGizmo';
import { JointBodyLines } from '../joint3d/JointBodyLines';
import { JointGizmoLines } from '../joint3d/JointGizmoLines';
import { sixDofBodySegments, sixDofJointSegments } from '../joint3d/jointGizmoDrawer';
import { useJointBodies } from '../joint3d/useJointBodies';
import { drawnLimits, sameLimits } from './types';
/** `Transform3D()`, the transform `redraw` passes for a body that does not resolve (`:446-447`). */
const UNRESOLVED_BODY = identityTransform3D();
/**
 * The bodies `redraw` draws circles for (`:450`): it hands body B's points over only when body A
 * resolves, and then draws body B toward the world origin where B itself does not resolve.
 */
function bodiesDrawn(bodies) {
    if (!bodies.bodyA)
        return { ...bodies, bodyB: null };
    return { ...bodies, bodyB: bodies.bodyB ?? UNRESOLVED_BODY };
}
function SixDofGizmo({ properties }) {
    const bodies = useJointBodies(properties);
    const limits = useByValue(drawnLimits(properties), sameLimits);
    const travel = useMemo(() => sixDofJointSegments(limits), [limits]);
    const segmentsFor = useCallback((joint, body, part) => sixDofBodySegments(joint, body, part, limits), [limits]);
    if (bodies === null)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx(JointGizmoLines, { part: "joint", positions: travel }), _jsx(JointBodyLines, { bodies: bodiesDrawn(bodies), segmentsFor: segmentsFor })] }));
}
export function Generic6DOFJoint3D({ node, children }) {
    return (_jsxs(Node3D, { node: node, children: [_jsx(PluginGizmo, { drawnFor: "every-node", children: _jsx(SixDofGizmo, { properties: node.properties }) }), children] }));
}
//# sourceMappingURL=Component.js.map